import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";
import type { PatientEmailHealth } from "../api/types.js";
import type { AdminApi } from "../api/client.js";
import { ApiError } from "../api/client.js";
import { Card } from "../components/Card.js";
import { Badge } from "../components/Badge.js";
import { ErrorBanner } from "../components/ErrorBanner.js";
import { staffTeamsChannelName } from "../lib/teams-staff-channel.js";

type Props = { api: AdminApi };

export function HealthPage({ api }: Props) {
  const queryClient = useQueryClient();
  const status = useQuery({
    queryKey: ["admin", "google-status"],
    queryFn: () => api.getGoogleStatus(),
  });
  const teams = useQuery({
    queryKey: ["admin", "teams-status"],
    queryFn: () => api.getTeamsStatus(),
  });
  const patientEmail = useQuery({
    queryKey: ["admin", "patient-email-status"],
    queryFn: () => api.getPatientEmailStatus(),
  });
  const testTeams = useMutation({
    mutationFn: () => api.testTeamsConnection(),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ["admin", "teams-status"] });
    },
  });

  const apiBaseUrl = import.meta.env.VITE_API_BASE_URL ?? "(unset)";
  const env = import.meta.env.VITE_APP_ENV ?? "(unset)";

  return (
    <div className="grid gap-4 lg:grid-cols-2">
      <Card title="Environment">
        <dl className="grid gap-2 text-sm">
          <Row label="Environment">
            <Badge tone={env === "production" ? "slate" : "rose"}>{env}</Badge>
          </Row>
          <Row label="API base">
            <code className="text-xs">{apiBaseUrl}</code>
          </Row>
        </dl>
      </Card>

      <Card title="Google Calendar">
        {status.isPending ? (
          <p className="text-sm text-slate-600">Checking…</p>
        ) : status.data?.connected ? (
          <dl className="grid gap-2 text-sm">
            <Row label="Status">
              <Badge tone="emerald">Connected</Badge>
            </Row>
            <Row label="By">{status.data.connected_by_email ?? "—"}</Row>
          </dl>
        ) : (
          <div className="grid gap-2 text-sm">
            <p>
              <Badge tone="amber">Not connected</Badge>
            </p>
            <p>
              <Link
                to="/settings/calendar"
                className="text-blue-700 hover:underline"
              >
                Connect Google Calendar →
              </Link>
            </p>
          </div>
        )}
      </Card>

      <Card title="Teams bot">
        {teams.isPending ? (
          <p className="text-sm text-slate-600">Checking…</p>
        ) : teams.data?.conversationRegistered ? (
          <div className="grid gap-3 text-sm">
            <dl className="grid gap-2">
              <Row label="Status">
                <Badge tone="emerald">Channel registered</Badge>
              </Row>
              <Row label="Delivery">
                {teams.data.deliveryVerified ? (
                  <Badge tone="emerald">Verified</Badge>
                ) : (
                  <Badge tone="amber">Not verified</Badge>
                )}
              </Row>
              <Row label="Card actions">
                {teams.data.teamsActionAuthConfigured ? (
                  <Badge tone="emerald">Graph auth configured</Badge>
                ) : (
                  <span className="text-rose-800">
                    Not configured — buttons will deny all
                    {(teams.data.teamsActionAuthMissing?.length ?? 0) > 0 ? (
                      <> (missing: {teams.data.teamsActionAuthMissing!.join(", ")})</>
                    ) : null}
                  </span>
                )}
              </Row>
              <Row label="Registered">
                {teams.data.installedAt
                  ? new Date(teams.data.installedAt).toLocaleString()
                  : "—"}
              </Row>
              <Row label="Last test">
                {teams.data.lastVerifiedAt
                  ? new Date(teams.data.lastVerifiedAt).toLocaleString()
                  : "Never"}
              </Row>
              {teams.data.expectedTeamId ? (
                <Row label="Team match">
                  {teams.data.teamIdMatches ? (
                    <Badge tone="emerald">Matches configured team</Badge>
                  ) : (teams.data.teamIdNeedsRefresh ?? false) ? (
                    <span className="text-slate-600">
                      Metadata only (channel id stored instead of team GUID) —
                      does not affect notifications.
                    </span>
                  ) : (
                    <span className="text-slate-600">
                      Metadata mismatch — does not affect notifications.
                    </span>
                  )}
                </Row>
              ) : null}
              {teams.data.teamId && teams.data.expectedTeamId ? (
                <Row label="Team ids">
                  <span className="font-mono text-xs text-slate-600">
                    registered {teams.data.teamId} · expected {teams.data.expectedTeamId}
                  </span>
                </Row>
              ) : null}
            </dl>
            <button
              type="button"
              disabled={testTeams.isPending}
              onClick={() => testTeams.mutate()}
              className="w-fit rounded-md border border-slate-300 px-3 py-1.5 text-sm font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50"
            >
              {testTeams.isPending ? "Sending test…" : "Test Teams connection"}
            </button>
            {testTeams.isSuccess ? (
              <p className="rounded-md border border-emerald-200 bg-emerald-50 px-3 py-2 text-emerald-900">
                Test card sent to the registered channel.
              </p>
            ) : null}
            {testTeams.isError ? (
              <ErrorBanner message={teamsTestErrorMessage(testTeams.error)} />
            ) : null}
          </div>
        ) : (
          <div className="grid gap-2 text-sm text-slate-700">
            <p>
              <Badge tone="amber">
                {teams.data?.botConfigured
                  ? "Not registered"
                  : "Not configured"}
              </Badge>
            </p>
            <p>
              The Azure Bot &quot;Test&quot; button only checks the messaging
              endpoint — it does not register a Teams channel. In the{" "}
              <strong>{staffTeamsChannelName()}</strong> team, remove and re-add
              the Book app to the channel where staff should see notifications.
              You can also post any message in that channel to refresh
              registration, then use &quot;Test Teams connection&quot; here.
            </p>
          </div>
        )}
      </Card>

      <Card title="Patient email consumer">
        {patientEmail.isPending ? (
          <p className="text-sm text-slate-600">Checking…</p>
        ) : patientEmail.data ? (
          <dl className="grid gap-2 text-sm">
            <Row label="Status">
              <PatientEmailHealthBadge health={patientEmail.data.health} />
            </Row>
            <Row label="ACS email">
              {patientEmail.data.emailConfigured ? (
                <Badge tone="emerald">Configured</Badge>
              ) : (
                <Badge tone="rose">Not configured</Badge>
              )}
            </Row>
            <Row label="Service Bus">
              {patientEmail.data.serviceBusConfigured ? (
                <Badge tone="emerald">Configured</Badge>
              ) : (
                <Badge tone="rose">Not configured</Badge>
              )}
            </Row>
            <Row label="Relay queue">
              {patientEmail.data.outboxPendingCount}{" "}
              {patientEmail.data.outboxPendingCount === 1 ? "event" : "events"}{" "}
              waiting to publish
            </Row>
            {patientEmail.data.outboxParkedCount > 0 ? (
              <Row label="Relay failures">
                <span className="text-rose-800">
                  {patientEmail.data.outboxParkedCount} parked — contact IT
                </span>
              </Row>
            ) : null}
            <Row label="Consumer queue">
              {patientEmail.data.subscriptionMetricsAvailable ? (
                <>
                  {patientEmail.data.subscriptionActiveCount ?? 0}{" "}
                  {(patientEmail.data.subscriptionActiveCount ?? 0) === 1
                    ? "message"
                    : "messages"}{" "}
                  on{" "}
                  <code className="text-xs">
                    {patientEmail.data.topic}/{patientEmail.data.subscription}
                  </code>
                </>
              ) : (
                <span className="text-slate-500">
                  Unavailable — relay queue count is still shown above
                </span>
              )}
            </Row>
            {patientEmail.data.subscriptionMetricsAvailable &&
            (patientEmail.data.subscriptionDeadLetterCount ?? 0) > 0 ? (
              <Row label="Dead letter">
                <span className="text-rose-800">
                  {patientEmail.data.subscriptionDeadLetterCount} message
                  {(patientEmail.data.subscriptionDeadLetterCount ?? 0) === 1
                    ? ""
                    : "s"}{" "}
                  — investigate immediately
                </span>
              </Row>
            ) : null}
            <Row label="Last email sent">
              {patientEmail.data.lastPatientEmailSentAt
                ? new Date(
                    patientEmail.data.lastPatientEmailSentAt,
                  ).toLocaleString()
                : "Never"}
            </Row>
            {patientEmail.data.health === "not_configured" ? (
              <Row label="Next step">
                <Link
                  to="/settings/test-email"
                  className="text-blue-700 hover:underline"
                >
                  Test email setup →
                </Link>
              </Row>
            ) : null}
          </dl>
        ) : patientEmail.isError ? (
          <ErrorBanner message={patientEmailErrorMessage(patientEmail.error)} />
        ) : (
          <ErrorBanner message="Could not load patient email status." />
        )}
      </Card>
    </div>
  );
}

function PatientEmailHealthBadge({ health }: { health: PatientEmailHealth }) {
  switch (health) {
    case "healthy":
      return <Badge tone="emerald">Healthy</Badge>;
    case "degraded":
      return <Badge tone="amber">Backlogged</Badge>;
    case "unhealthy":
      return <Badge tone="rose">Unhealthy</Badge>;
    case "not_configured":
      return <Badge tone="amber">Not configured</Badge>;
  }
}

function patientEmailErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "route_not_found" || error.status === 404) {
      return "Patient email status is not available on this API revision yet. Wait for the API deploy to finish, then refresh.";
    }
    return error.message || "Could not load patient email status.";
  }
  return "Could not load patient email status.";
}

function teamsTestErrorMessage(error: unknown): string {
  if (error instanceof ApiError) {
    switch (error.code) {
      case "no_teams_conversation":
        return "No Teams channel is registered yet. Re-add the Book app to the staff channel.";
      case "incomplete_teams_reference":
        return "Registration is incomplete. Re-add the Book app to the staff channel.";
      case "unexpected_teams_team":
        return "The registered channel is in the wrong Teams team. Re-add the app in the correct team.";
      case "teams_not_configured":
        return "Teams bot credentials are not configured on the API.";
      case "teams_send_failed":
        return "Could not deliver the test message to Teams.";
      default:
        return error.message || "Teams test failed.";
    }
  }
  return "Teams test failed.";
}

function Row({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <div className="grid grid-cols-[8rem_1fr] gap-3">
      <dt className="text-slate-500">{label}</dt>
      <dd className="text-slate-800">{children}</dd>
    </div>
  );
}
